// The app on a touch phone (Poulpe-624): the phone's screens in place of the
// desktop's header and pages, on the same store and the same addresses
// where the desktop has them. App.tsx draws it on a touch phone
// (usePhone.ts), outside the Mac app's workspace, unless the person chose
// the full board; signing in stays the app's own.
//
// The phone shows what needs the person and what they need to decide: Needs
// you, a question, an approval with what it changes (approved with a
// passkey, Poulpe-625), the board, read only (board.tsx, Poulpe-881), the digest, an issue's summary and a session's facts
// (context.tsx), read only. Any other address says it is
// on a computer, with the way back to Needs you and the full board: no phone
// screen controls an agent, a terminal or a runner. The account menu, on the
// screens with the logo, says who is signed in, signs out, and opens the
// full board.
import { Route, Routes, useLocation } from 'react-router-dom'
import { ErrorState, Loading, PageBoundary } from '../components/layout'
import { Toaster } from '../components/toasts'
import { EmailLinkPage } from '../people/EmailSignIn'
import { useBoardState, useStore } from '../store/context'
import { ProjectRoute } from '../projects/ProjectRoute'
import { ApprovalScreen } from './ApprovalScreen'
import { BoardScreen } from './board'
import { ChangesScreen, FileScreen } from './changes'
import { DigestScreen, IssueScreen, SessionScreen } from './context'
import { NeedsYou } from './NeedsYou'
import { QuestionScreen } from './QuestionScreen'
import { Note, Screen, TopBar } from './shared'
import { chooseFullBoard } from './usePhone'
import './phone.css'

/** The phone's screens. The answers on their way are App.tsx's (`AnswerKeeper`), above the choice of layout. */
export function PhoneFrame() {
  const store = useStore()
  const { status, error } = useBoardState()
  const { pathname } = useLocation()
  let body
  // Loading and an error carry the logo too, in a top bar with no title (Poulpe-880).
  if (status === 'loading') body = <Screen top={<TopBar back={null} brand />}><Loading /></Screen>
  else if (status === 'error') body = <Screen top={<TopBar back={null} brand />}><ErrorState error={error} onRetry={() => void store.load()} /></Screen>
  else
    body = (
      // A screen that fails shows a way out, as a desktop page does.
      <PageBoundary resetKey={pathname}>
        <Routes>
          {['/', '/needs-you'].map((path) => <Route key={path} path={path} element={<NeedsYou />} />)}
          <Route path="/q/:id" element={<QuestionScreen />} />
          <Route path="/approval/:id" element={<ApprovalScreen />} />
          <Route path="/approval/:id/changes" element={<ChangesScreen />} />
          <Route path="/approval/:id/changes/:proposal/file" element={<FileScreen />} />
          <Route path="/digest" element={<DigestScreen />} />
          {/* The board (Poulpe-881), at the desktop's own addresses; a project's selects it, as the desktop's does. */}
          <Route path="/board" element={<BoardScreen />} />
          <Route path="/p/:project/board" element={<ProjectRoute><BoardScreen /></ProjectRoute>} />
          {['/sessions/:id', '/p/:project/code/:id'].map((path) => <Route key={path} path={path} element={<SessionScreen />} />)}
          <Route path="/:key" element={<IssueScreen elsewhere={<OnComputer />} />} />
          <Route path="/signin/:attempt" element={<EmailLinkPage />} />
          <Route path="*" element={<OnComputer />} />
        </Routes>
      </PageBoundary>
    )
  return (
    <div className="app m-app" data-layout="phone">
      {body}
      <Toaster />
    </div>
  )
}

/** On a phone that chose the full board: the way back to the phone's screens, over the board. */
export function BackToPhone() {
  return (
    <div className="m-phone-switch">
      <button type="button" className="button" onClick={() => chooseFullBoard(false)}>Phone view</button>
    </div>
  )
}

/** An address the phone does not draw. */
function OnComputer() {
  return (
    <Screen top={<TopBar title="On a computer" />}>
      <Note>This page is on a computer. On the phone, Poulpe shows what needs you, and what you need to know to decide.</Note>
      <button type="button" className="button m-target" onClick={() => chooseFullBoard(true)}>Open the full board here</button>
    </Screen>
  )
}
